import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { canRaiseInvoiceFromDelivery, hasFinancialAccess } from '../utils/financialAccess';
import './InvoicePanel.css';
import { CloseIconSmall } from './ChecklistIcons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faFileInvoice, faHistory, faChevronRight, faFileContract, faFileImport, faUndoAlt, faExclamationCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import invoiceData from './invoiceData';
import invoicingService from '../services/invoicingService';
import { useFlashMessage } from './FlashMessage';
import PartnerSelectionModal from './Invoicing/PartnerSelectionModal';
import RecapSelectionModal from './Invoicing/RecapSelectionModal';
import { bdnQuantityNote } from '../utils/bdnQuantityNote';

// Map icon names to FontAwesome icon objects
const iconMap = {
  'faFileInvoiceDollar': faFileInvoiceDollar,
  'faFileInvoice': faFileInvoice,
  'faHistory': faHistory,
  'faFileContract': faFileContract,
  'faFileImport': faFileImport,
  'faUndoAlt': faUndoAlt,
  'faExclamationCircle': faExclamationCircle,
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16).slice(1);
};

// Helper function to convert hex to rgba
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const InvoiceOption = ({ id, title, description, icon, onClick, isDisabled, disabledReason, color }) => {
  const iconObject = iconMap[icon] || faFileInvoice;
  
  return (
    <div 
      className={`invoice-option ${isDisabled ? 'invoice-option-disabled' : ''}`} 
      onClick={isDisabled ? undefined : onClick}
      // A greyed-out button with no reason reads as broken rather than as a
      // permission. ``disabledReason`` was computed and thrown away before this
      // tier existed; now that operators see these options disabled by default,
      // it has to reach them.
      title={isDisabled ? disabledReason : undefined}
      style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.6 : 1 }}
    >
      <div 
        className="invoice-option-icon"
        style={color ? {
          background: `linear-gradient(135deg, ${color} 0%, ${adjustColorBrightness(color, -20)} 100%)`,
          boxShadow: `0 2px 8px ${hexToRgba(color, 0.2)}`
        } : {}}
      >
        <FontAwesomeIcon icon={iconObject} />
      </div>
      <div className="invoice-option-content">
        <h3>{title}</h3>
        <p className="invoice-option-description">{description}</p>
      </div>
      <div className="invoice-option-action">
        <button 
          className="invoice-navigate-button" 
          aria-label={`Navigate to ${title}`}
          disabled={isDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
};

const InvoiceSelectionModal = ({ isOpen, onClose, invoices, onSelect, type }) => {
  if (!isOpen) return null;
  
  return (
    <div className="invoice-selection-overlay" onClick={onClose}>
      <div className="invoice-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Invoice to Adjust</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-body">
          <p className="modal-description">
            Select which invoice you want to {type === 'credit' ? 'credit' : 'debit'}:
          </p>
          <div className="invoice-selection-list">
            {invoices.map(invoice => (
              <div 
                key={invoice.id}
                className="invoice-selection-item"
                onClick={() => onSelect(invoice.id)}
              >
                <div className="invoice-info">
                  <span className="invoice-number">{invoice.invoice_number}</span>
                  <span className="invoice-status">{invoice.status}</span>
                </div>
                <div className="invoice-amount">
                  {invoice.currency} {invoice.gross_amount}
                </div>
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * What went wrong with one of the six list loads — refused, truncated, or just
 * broken. Never "empty": emptiness is what a SUCCESSFUL load with no rows
 * looks like, and conflating the two is the bug this panel was reported for.
 *
 * The three kinds are read off the rejection itself rather than through a
 * service helper, because the error is the contract:
 *   - `isListTruncation` is the public flag on `InvoiceListTruncatedError`,
 *     thrown by the list-completeness guard in invoicingService. It carries its
 *     own `userMessage` because only the guard knows how many rows are missing.
 *   - `response.status` is axios's, for a refusal.
 * There is no fourth branch and no silent default: anything unrecognised is
 * still reported as a failure, because the one thing we must never do here is
 * fall through to an empty list.
 */
const classifyInvoiceLoadFailure = (error) => {
  if (error && error.isListTruncation) {
    return {
      kind: 'truncated',
      message: error.userMessage || error.message,
    };
  }
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return {
      kind: 'refused',
      message:
        'Invoices for this delivery could not be loaded because your account was '
        + 'refused access to them. Ask an administrator for Financials access, or '
        + 'for this delivery to be added to your operations grouping.',
    };
  }
  return {
    kind: 'failed',
    message:
      `Invoices for this delivery could not be loaded${status ? ` (the server answered ${status})` : ''}. `
      + 'That is not the same as there being none — try again, and tell IT if it keeps happening.',
  };
};

/* Which failure wins when several of the six lists fail at once. A refusal is
 * the most actionable thing we can say; a truncation is a wrong-number warning;
 * a generic breakage is the least specific of the three. */
const FAILURE_SEVERITY = { refused: 3, truncated: 2, failed: 1 };

const worstFailure = (failures) => {
  if (!failures.length) return null;
  return failures.reduce((worst, candidate) => (
    FAILURE_SEVERITY[candidate.kind] > FAILURE_SEVERITY[worst.kind] ? candidate : worst
  ));
};

const InvoicePanel = ({ isOpen, onClose, deliveryId, deliveryStatus }) => {
  const history = useHistory();
  const { showError, showSuccess } = useFlashMessage();
  const { user } = useContext(AuthContext);
  // Raising an invoice from THIS delivery is open to the trade-ops operator who
  // ran it as well as to finance (Craig, 2026-09-04) -- the operator who books
  // the bunkers is the one who invoices them, and sending them off to ask for a
  // Financials grant first was the reported bug. Backend twin:
  // invoicing.delivery_scoped_access.
  const canWriteInvoices = canRaiseInvoiceFromDelivery(user);
  // Blessing a document is a different question and stays finance's: approve /
  // reject / void / send / record-payment. Only used for the affordances that
  // lead to those; the buttons themselves live on the invoice screens.
  const canApproveInvoices = hasFinancialAccess(user);
  const [linkedInvoices, setLinkedInvoices] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  // A failed load is NOT an empty list. See loadLinkedInvoices.
  //
  // `null` when all six lists answered. Otherwise the WORST of what went wrong,
  // as {kind: 'refused' | 'truncated' | 'failed', message}. It used to be a
  // bare `refused` boolean, which only knew about 403/401 -- so the day the
  // list-completeness guard threw (a plain Error, no `.response`), the panel
  // classified it as "not a refusal", filled the list with [] and drew an empty
  // panel with no explanation. That is the same swallow this panel was reported
  // for, wearing the costume of the guard that was meant to prevent it.
  const [loadProblem, setLoadProblem] = useState(null);
  const loadRefused = loadProblem?.kind === 'refused';
  const [showInvoiceSelectionModal, setShowInvoiceSelectionModal] = useState(false);
  const [selectedAdjustmentType, setSelectedAdjustmentType] = useState(null);
  
  // Proforma conversion choice state
  const [showProformaChoiceModal, setShowProformaChoiceModal] = useState(false);
  const [pendingProforma, setPendingProforma] = useState(null);

  // Partner and Recap selection flow state
  const [showPartnerSelectionModal, setShowPartnerSelectionModal] = useState(false);
  const [showRecapSelectionModal, setShowRecapSelectionModal] = useState(false);
  const [pendingInvoiceOption, setPendingInvoiceOption] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedRecapIds, setSelectedRecapIds] = useState(null);

  // Bills to receive: who still has to bill us for this delivery. `null` until
  // loaded; a failed load is said out loud, never drawn as "nothing to bill".
  const [bills, setBills] = useState(null);
  const [billsProblem, setBillsProblem] = useState(null);
  // The purchase document (supplier invoice or purchase order) waiting on a
  // supplier choice, or null when the supplier picker is closed.
  const [billSupplierOption, setBillSupplierOption] = useState(null);

  /**
   * Tack this delivery on as the return address.
   *
   * Every /invoicing/* link raised from this panel gets it, so the document
   * screen's back arrow and its post-save redirect both come home here instead
   * of to the Finance invoice table. See shared/useDocumentReturn.js for the
   * reading end.
   */
  const withReturnToDelivery = (path) => (
    deliveryId
      ? `${path}${path.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(`/deliveries/${deliveryId}`)}`
      : path
  );

  const loadLinkedInvoices = async () => {
    if (!deliveryId) {
      console.log('[InvoicePanel] No deliveryId, skipping load');
      return;
    }
    
    console.log('[InvoicePanel] Loading invoices for delivery:', deliveryId);
    setLoadingInvoices(true);
    setLoadProblem(null);

    // ``allSettled``, not ``all``, and the rejections are INSPECTED rather than
    // collapsed. The original code awaited Promise.all inside a try/catch whose
    // catch set all six lists to [] -- so six 403s rendered as "no invoices
    // linked yet" and the operator concluded nothing had been invoiced. That is
    // the bug this panel was reported for (ledger row 71). One list failing
    // must also not blank the other five.
    const requests = [
      ['proformas', () => invoicingService.fetchProformaInvoices({ source_delivery: deliveryId })],
      ['outgoing', () => invoicingService.fetchOutgoingInvoices({ source_delivery: deliveryId })],
      ['purchaseOrders', () => invoicingService.fetchPurchaseOrders({ source_delivery: deliveryId })],
      ['incoming', () => invoicingService.fetchIncomingInvoices({ source_delivery: deliveryId })],
      ['creditNotes', () => invoicingService.fetchCreditNotes({ source_delivery: deliveryId })],
      ['debitNotes', () => invoicingService.fetchDebitNotes({ source_delivery: deliveryId })],
    ];

    try {
      const settled = await Promise.allSettled(requests.map(([, call]) => call()));
      const linkedData = {};
      const failures = [];

      settled.forEach((result, index) => {
        const [key] = requests[index];
        if (result.status === 'fulfilled') {
          const payload = result.value;
          linkedData[key] = Array.isArray(payload) ? payload : (payload?.results || []);
          return;
        }
        // Rejected: an empty list is a LIE here, so say WHICH kind of failure
        // it was. Every rejection is classified -- refused, truncated, or just
        // broken -- and every one of the three is shown. Nothing may reach the
        // `= []` below without also putting a sentence on the screen.
        const failure = classifyInvoiceLoadFailure(result.reason);
        failures.push(failure);
        console.error(`[InvoicePanel] ${key} failed (${failure.kind}):`, result.reason);
        linkedData[key] = [];
      });

      setLinkedInvoices(linkedData);
      setLoadProblem(worstFailure(failures));
    } finally {
      setLoadingInvoices(false);
    }
  };

  const getApprovedARInvoices = () => {
    if (!linkedInvoices?.outgoing) return [];
    return linkedInvoices.outgoing.filter(inv => 
      ['APPROVED', 'SENT', 'PAID', 'OVERDUE'].includes(inv.status)
    );
  };

  const getApprovedAPInvoices = () => {
    if (!linkedInvoices?.incoming) return [];
    return linkedInvoices.incoming.filter(inv => 
      ['APPROVED', 'SENT', 'PAID', 'OVERDUE'].includes(inv.status)
    );
  };

  // Handler for invoice selection
  const handleInvoiceSelected = (invoiceId) => {
    setShowInvoiceSelectionModal(false);
    
    const path = selectedAdjustmentType === 'credit' 
      ? `/invoicing/credit-notes/create?invoiceId=${invoiceId}`
      : `/invoicing/debit-notes/create?invoiceId=${invoiceId}`;
    
    history.push(withReturnToDelivery(path));
    onClose();
  };

  const loadBills = async () => {
    if (!deliveryId || !canWriteInvoices) return;
    setBillsProblem(null);
    try {
      setBills(await invoicingService.fetchDeliveryBills(deliveryId));
    } catch (error) {
      console.error('[InvoicePanel] bills to receive failed:', error);
      setBills(null);
      setBillsProblem(classifyInvoiceLoadFailure(error));
    }
  };

  // Load linked invoices when panel opens
  useEffect(() => {
    if (isOpen && deliveryId) {
      loadLinkedInvoices();
      loadBills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deliveryId]);

  // If panel is closed, don't render anything
  if (!isOpen) return null;

  const handleOptionClick = async (option) => {
    // Check if delivery has been workflowed (status is ACTIVE)
    if (deliveryStatus !== 'ACTIVE') {
      showError(
        'This delivery must be finalized via "Save & Workflow" before creating invoices. Please workflow the delivery first.',
        7000
      );
      return;
    }
    
    // Handle Credit/Debit Note clicks
    if (option.id === 'issue_credit_note' || option.id === 'issue_debit_note') {
      const approvedInvoices = option.id === 'issue_credit_note'
        ? getApprovedARInvoices()
        : getApprovedAPInvoices();
      
      if (approvedInvoices.length === 0) {
        const side = option.id === 'issue_credit_note' ? 'outgoing' : 'incoming';
        showError(
          `No approved ${side} invoices found. Invoices must be APPROVED, SENT, PAID, or OVERDUE to issue adjustment notes.`,
          7000
        );
        return;
      }
      
      if (approvedInvoices.length === 1) {
        // Navigate directly with single invoice
        const invoiceId = approvedInvoices[0].id;
        const path = `${option.navigationPath}?invoiceId=${invoiceId}`;
        history.push(withReturnToDelivery(path));
        onClose();
        return;
      }
      
      // Multiple invoices - show selection modal
      setSelectedAdjustmentType(option.id === 'issue_credit_note' ? 'credit' : 'debit');
      setShowInvoiceSelectionModal(true);
      return;
    }
    
    // Handle Issue Final Invoice - check for unconverted proformas
    if (option.id === 'issue_final_invoice') {
      const unconvertedProformas = (linkedInvoices?.proformas || []).filter(p => !p.converted_to_outgoing);
      
      if (unconvertedProformas.length > 0) {
        setPendingProforma(unconvertedProformas[0]);
        setShowProformaChoiceModal(true);
        return;
      }
      // No unconverted proformas -- fall through to create directly
    }
    
    // A supplier invoice or purchase order belongs to ONE supplier, and a
    // purchase recap names a supplier per product line -- the delivery's
    // contractual list only knows the recap header, so it offered one supplier
    // on a two-supplier deal.
    if (option.id === 'capture_incoming_invoice' || option.id === 'create_purchase_order') {
      const named = billSuppliers();
      if (named.length > 1) {
        setBillSupplierOption(option);
        return;
      }
      if (named.length === 1) {
        startSupplierInvoice(named[0], option);
        return;
      }
    }

    // For invoice/PO creation options, check for multiple partners/recaps
    const needsPartnerRecapSelection = [
      'issue_proforma_invoice',
      'issue_final_invoice',
      'create_purchase_order',
      'capture_incoming_invoice'
    ].includes(option.id);
    
    if (needsPartnerRecapSelection) {
      // Show loading state
      setLoadingInvoices(true);
      
      try {
        // Fetch delivery details to check for multiple partners/recaps
        const delivery = await invoicingService.fetchDeliveryById(deliveryId);
        setDeliveryData(delivery);
        setPendingInvoiceOption(option);
        
        // Determine partner type and recap type based on option
        const isAR = option.id === 'issue_proforma_invoice' || option.id === 'issue_final_invoice';
        const partnerList = isAR ? (delivery.buyer_contractual_list || []) : (delivery.seller_contractual_list || []);
        const recaps = isAR ? (delivery.sales_recaps_data || []) : (delivery.purchase_recaps_data || []);
        
        // Check if multiple partners
        if (partnerList && partnerList.length > 1) {
          setShowPartnerSelectionModal(true);
          return;
        }
        
        // Single partner or no partners - get partner ID if available
        const partnerId = partnerList && partnerList.length === 1 ? partnerList[0].crm_id : null;
        setSelectedPartnerId(partnerId);
        
        // Check if multiple recaps
        if (recaps && recaps.length > 1) {
          setShowRecapSelectionModal(true);
          return;
        }
        
        // Single recap or no recaps - navigate with all parameters
        const recapIds = recaps && recaps.length === 1 ? [recaps[0].id] : null;
        navigateToInvoiceForm(option, partnerId, recapIds);
        
      } catch (error) {
        console.error('[InvoicePanel] Error fetching delivery details:', error);
        showError('Failed to load delivery details. Please try again.');
      } finally {
        setLoadingInvoices(false);
      }
      return;
    }
    
    // For other options (view history, etc.), navigate directly
    const path = `${option.navigationPath}?deliveryId=${deliveryId}`;
    history.push(path);
    onClose();
  };
  
  // Handle partner selection completion
  const handlePartnerSelected = (partnerId) => {
    setSelectedPartnerId(partnerId);
    setShowPartnerSelectionModal(false);
    
    // Check if multiple recaps
    const isAR = pendingInvoiceOption.id === 'issue_proforma_invoice' || 
                 pendingInvoiceOption.id === 'issue_final_invoice';
    const recaps = isAR ? (deliveryData.sales_recaps_data || []) : (deliveryData.purchase_recaps_data || []);
    
    if (recaps && recaps.length > 1) {
      setShowRecapSelectionModal(true);
    } else {
      // Single or no recaps - navigate
      const recapIds = recaps && recaps.length === 1 ? [recaps[0].id] : null;
      navigateToInvoiceForm(pendingInvoiceOption, partnerId, recapIds);
    }
  };
  
  // Handle recap selection completion
  const handleRecapSelected = (recapIds) => {
    setSelectedRecapIds(recapIds);
    setShowRecapSelectionModal(false);
    navigateToInvoiceForm(pendingInvoiceOption, selectedPartnerId, recapIds);
  };
  
  // Navigate to invoice form with partner and recap IDs
  const navigateToInvoiceForm = (option, partnerId, recapIds) => {
    try {
      if (!option || !option.navigationPath) {
        showError('Invalid invoice option. Please try again.');
        return;
      }
      
      let path = `${option.navigationPath}?deliveryId=${deliveryId}`;
      
      if (partnerId) {
        path += `&partnerId=${partnerId}`;
      }
      
      if (recapIds && recapIds.length > 0) {
        path += `&recapIds=${recapIds.join(',')}`;
      }
      
      history.push(path);
      
      // Clean up state
      setDeliveryData(null);
      setPendingInvoiceOption(null);
      setSelectedPartnerId(null);
      setSelectedRecapIds(null);
      
      onClose();
    } catch (error) {
      console.error('[InvoicePanel] Navigation error:', error);
      showError('Failed to navigate to invoice form. Please try again.');
    }
  };
  
  const captureOption = () => invoiceData.options.find((o) => o.id === 'capture_incoming_invoice');

  // Suppliers of bought products that are actually named on the deal.
  const billSuppliers = () => (bills?.suppliers || []).filter((s) => s.supplier_id);

  const startSupplierInvoice = (group, option = captureOption()) => {
    navigateToInvoiceForm(option, group.supplier_id, group.recap_ids);
  };

  const startCostInvoice = (cost) => {
    history.push(`${captureOption().navigationPath}?deliveryId=${deliveryId}&thirdPartyCostId=${cost.id}`);
    onClose();
  };

  // Row affordance. The single-document /invoicing/* routes now open for
  // trade-ops operators too (ProtectedRoute requiresFinancialOrTradeOpsAccess),
  // so anyone who may raise an invoice here may also click through to one.
  // Anyone else keeps the number, status and amount, which is what "can I see
  // the invoice for this delivery" actually asks.
  //
  // **2026-09-09:** the link now carries where to come BACK to. Opening an
  // invoice from this panel and then closing it used to land the user on the
  // Financials invoice table -- a screen an operator cannot open at all. The
  // origin travels in the address bar rather than in route state so it also
  // survives a refresh or a shared link.
  const invoiceRowProps = (path) => (canWriteInvoices
    ? {
        onClick: () => { history.push(withReturnToDelivery(path)); onClose(); },
        style: { cursor: 'pointer' },
      }
    : {
        style: { cursor: 'default' },
        title: 'View-only from the delivery. Opening the full invoice needs Financials access.',
      });

  const renderLinkedInvoices = () => (
    <div className="invoice-history-section">
      <h3>Linked Invoices</h3>
      {linkedInvoices.proformas?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Proforma Invoices ({linkedInvoices.proformas.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.proformas.map((inv) => (
              <li 
                key={inv.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/proforma/${inv.id}`)}
              >
                <span className="invoice-number">{inv.invoice_number}</span>
                <span className="invoice-partner">{inv.partner_name_display || inv.partner_name}</span>
                <span className="invoice-status">{inv.status}</span>
                <span className="invoice-amount">{inv.currency} {inv.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkedInvoices.outgoing?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Final Invoices ({linkedInvoices.outgoing.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.outgoing.map((inv) => (
              <li 
                key={inv.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/outgoing/${inv.id}`)}
              >
                <span className="invoice-number">{inv.invoice_number}</span>
                <span className="invoice-partner">{inv.partner_name_display || inv.partner_name}</span>
                <span className="invoice-status">{inv.status}</span>
                <span className="invoice-amount">{inv.currency} {inv.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkedInvoices.purchaseOrders?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Purchase Orders ({linkedInvoices.purchaseOrders.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.purchaseOrders.map((po) => (
              <li 
                key={po.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/purchase-orders/${po.id}`)}
              >
                <span className="invoice-number">{po.po_number}</span>
                <span className="invoice-partner">{po.supplier_name_display || po.supplier_name}</span>
                <span className="invoice-status">{po.status}</span>
                <span className="invoice-amount">{po.currency} {po.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkedInvoices.incoming?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Incoming Invoices ({linkedInvoices.incoming.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.incoming.map((inv) => (
              <li 
                key={inv.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/incoming/${inv.id}`)}
              >
                <span className="invoice-number">{inv.hms_expense_number}</span>
                <span className="invoice-partner">{inv.supplier_name_display || inv.partner_name}</span>
                <span className="invoice-status">{inv.status}</span>
                <span className="invoice-amount">{inv.currency} {inv.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkedInvoices.creditNotes?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Credit Notes ({linkedInvoices.creditNotes.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.creditNotes.map((cn) => (
              <li 
                key={cn.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/credit-notes/${cn.id}`)}
              >
                <span className="invoice-number">{cn.credit_note_number}</span>
                <span className="invoice-partner">{cn.partner_name_display || cn.partner_name}</span>
                <span className="invoice-status">{cn.status}</span>
                <span className="invoice-amount">{cn.currency} {cn.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkedInvoices.debitNotes?.length > 0 && (
        <div className="invoice-history-group">
          <h4>Debit Notes ({linkedInvoices.debitNotes.length})</h4>
          <ul className="invoice-history-list">
            {linkedInvoices.debitNotes.map((dn) => (
              <li 
                key={dn.id} 
                className="invoice-history-item"
                {...invoiceRowProps(`/invoicing/debit-notes/${dn.id}`)}
              >
                <span className="invoice-number">{dn.debit_note_number}</span>
                <span className="invoice-partner">{dn.partner_name_display || dn.partner_name}</span>
                <span className="invoice-status">{dn.status}</span>
                <span className="invoice-amount">{dn.currency} {dn.gross_amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Why a bill row cannot start an invoice right now, or '' when it can.
  // Same two rules as the Capture Incoming Invoice button.
  const billBlockedReason = () => {
    if (!canWriteInvoices) return 'Financials or Trade Ops access required to raise invoices';
    if (deliveryStatus !== 'ACTIVE') return 'Delivery must be workflowed first';
    return '';
  };

  const BillStatus = ({ invoices }) => (
    invoices.length === 0 ? (
      <span className="bill-status bill-status-outstanding">Outstanding</span>
    ) : (
      <span className="bill-status-list">
        {invoices.map((inv) => (
          <span
            key={inv.id}
            className={`bill-status bill-status-${inv.status.toLowerCase()}`}
            title={inv.supplier_invoice_number ? `Supplier invoice ${inv.supplier_invoice_number}` : undefined}
            {...invoiceRowProps(`/invoicing/incoming/${inv.id}`)}
          >
            {inv.hms_expense_number} · {inv.status_display}
          </span>
        ))}
      </span>
    )
  );

  const renderBillsToReceive = () => {
    if (!canWriteInvoices) return null;
    const blocked = billBlockedReason();
    const suppliers = bills?.suppliers || [];
    const costs = bills?.costs || [];
    const namedCount = suppliers.filter((s) => s.supplier_id).length;

    const rowProps = (canStart, onStart, reason) => (
      canStart && !blocked
        ? { onClick: onStart, role: 'button', tabIndex: 0, className: 'bill-row bill-row-startable', title: 'Start the supplier invoice for this' }
        : { className: 'bill-row', title: reason || blocked || undefined }
    );

    return (
      <div className="invoice-history-section bills-to-receive-section">
        <h3>Bills to Receive</h3>
        {billsProblem ? (
          <p className="no-invoices-text">
            <FontAwesomeIcon icon={faExclamationCircle} />{' '}
            {billsProblem.kind === 'refused'
              ? 'You are not allowed to see the supplier bills for this delivery.'
              : 'The supplier bills for this delivery could not be loaded. Try again, and tell IT if it keeps happening.'}
          </p>
        ) : !bills ? (
          <p className="loading-text">Loading bills...</p>
        ) : (
          <>
            <div className="invoice-history-group">
              <h4>Products bought ({suppliers.length} {suppliers.length === 1 ? 'supplier' : 'suppliers'})</h4>
              {suppliers.length === 0 ? (
                <p className="no-invoices-text">No purchase recap is linked to this delivery.</p>
              ) : (
                <ul className="invoice-history-list">
                  {suppliers.map((group) => {
                    const canStart = group.invoices.length === 0 && (group.supplier_id || namedCount <= 1);
                    return (
                      <li
                        key={group.supplier_id || 'unnamed'}
                        {...rowProps(canStart, () => startSupplierInvoice(group),
                          group.invoices.length ? '' : 'Enter the supplier on the purchase recap first')}
                      >
                        <div className="bill-main">
                          <span className="bill-title">{group.supplier_name || 'Supplier not entered'}</span>
                          <span className="bill-detail">
                            {group.products.map((p) => {
                              // From the BDN, or the contract while the BDN is not in (ledger 166).
                              const bdn = bdnQuantityNote(p.quantity, p.bdn_check, p.unit || 'MT');
                              return `${p.description} · ${p.quantity ?? '?'} ${p.unit}`
                                + `${p.missing.length ? ` (no ${p.missing.join('/')} on the deal)` : ''}`
                                + `${bdn ? ` (${bdn.text})` : ''}`;
                            }).join('  |  ')}
                          </span>
                        </div>
                        <span className="invoice-amount">
                          {Object.entries(group.totals).map(([cur, total]) => `${cur} ${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`).join(' + ') || '—'}
                        </span>
                        <BillStatus invoices={group.invoices} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="invoice-history-group">
              <h4>Third-party costs ({costs.length})</h4>
              {costs.length === 0 ? (
                <p className="no-invoices-text">No third-party costs were entered on this deal.</p>
              ) : (
                <ul className="invoice-history-list">
                  {costs.map((cost) => (
                    <li key={cost.id} {...rowProps(cost.invoices.length === 0, () => startCostInvoice(cost), '')}>
                      <div className="bill-main">
                        <span className="bill-title">{cost.category}</span>
                        <span className="bill-detail">
                          {cost.charge_mode_display} · Supplier: {cost.supplier_name || '—'}
                          {cost.shared_with_deliveries ? ` · shared with ${cost.shared_with_deliveries - 1} other deliveries` : ''}
                        </span>
                      </div>
                      <span className="invoice-amount">
                        {cost.cost_amount != null ? `${cost.currency} ${Number(cost.cost_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'No amount'}
                      </span>
                      <BillStatus invoices={cost.invoices} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const handleProformaConvert = () => {
    if (pendingProforma) {
      history.push(withReturnToDelivery(`/invoicing/proforma/${pendingProforma.id}`));
      setShowProformaChoiceModal(false);
      setPendingProforma(null);
      onClose();
    }
  };

  const handleCreateNewOutgoing = async () => {
    setShowProformaChoiceModal(false);
    setPendingProforma(null);
    const finalOption = invoiceData.options.find(o => o.id === 'issue_final_invoice');
    if (!finalOption) return;

    setLoadingInvoices(true);
    try {
      const delivery = await invoicingService.fetchDeliveryById(deliveryId);
      setDeliveryData(delivery);
      setPendingInvoiceOption(finalOption);

      const partnerList = delivery.buyer_contractual_list || [];
      const recaps = delivery.sales_recaps_data || [];

      if (partnerList.length > 1) {
        setShowPartnerSelectionModal(true);
        return;
      }

      const partnerId = partnerList.length === 1 ? partnerList[0].crm_id : null;
      setSelectedPartnerId(partnerId);

      if (recaps.length > 1) {
        setShowRecapSelectionModal(true);
        return;
      }

      const recapIds = recaps.length === 1 ? [recaps[0].id] : null;
      navigateToInvoiceForm(finalOption, partnerId, recapIds);
    } catch (error) {
      console.error('[InvoicePanel] Error fetching delivery details:', error);
      showError('Failed to load delivery details. Please try again.');
    } finally {
      setLoadingInvoices(false);
    }
  };

  return (
    <div className="invoice-panel-overlay">
      <div 
        className="invoice-panel"
        role="dialog"
        aria-labelledby="invoice-panel-title"
      >
        <div className="invoice-panel-header">
          <h2 id="invoice-panel-title">Invoice Management</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close invoice panel"
          >
            <CloseIconSmall />
          </button>
        </div>
        
        <div className="invoice-panel-content">
          {/* Whatever went wrong with the load, said out loud and at the TOP.
              It has to live here rather than only inside the empty state,
              because a truncated or refused list can sit alongside five lists
              that answered -- and in that case the empty state never renders,
              so the only place the operator could have been told is a place
              that is not on the screen. Silence there is the same wrong number
              as silence anywhere else. */}
          {loadProblem && (
            <div
              role="alert"
              className="invoice-load-problem"
              style={{
                margin: '0 0 1rem',
                padding: '0.85rem 1rem',
                border: '1px solid #f59e0b',
                borderLeft: '4px solid #b45309',
                borderRadius: '6px',
                background: '#fffbeb',
                color: '#78350f',
                fontSize: '0.9rem',
              }}
            >
              <FontAwesomeIcon icon={faExclamationCircle} />{' '}
              <strong style={{ marginRight: '0.4rem' }}>
                {loadProblem.kind === 'truncated'
                  ? 'This is not the whole list.'
                  : 'The invoice list could not be loaded.'}
              </strong>
              {loadProblem.message}
            </div>
          )}
          {/* Why the options below are off. ACCESS is checked first because it
              is the reason that will not go away: telling a read-only operator
              to workflow the delivery sends them to do work that changes
              nothing, which is the same "wrong answer, confidently given" shape
              as the empty list this panel was reported for.

              Suppressed once the load was REFUSED: promising view-only access
              to someone who was just refused outright is the same lie in the
              other direction. The refusal message below is then the only
              answer they get. */}
          {!canWriteInvoices && !loadRefused ? (
            <div className="invoice-helper-text">
              <FontAwesomeIcon icon={faFileInvoice} />
              <p>
                You have view-only access to invoices from this delivery. Raising or
                editing one needs Financials access.
              </p>
            </div>
          ) : deliveryStatus !== 'ACTIVE' && (
            <div className="invoice-helper-text">
              <FontAwesomeIcon icon={faFileInvoice} />
              <p>Once delivery has been saved and approved, invoicing options become available</p>
            </div>
          )}
          
          {/* Linked Invoices first if any exist */}
          {linkedInvoices && (
            linkedInvoices.proformas?.length > 0 ||
            linkedInvoices.outgoing?.length > 0 ||
            linkedInvoices.purchaseOrders?.length > 0 ||
            linkedInvoices.incoming?.length > 0 ||
            linkedInvoices.creditNotes?.length > 0 ||
            linkedInvoices.debitNotes?.length > 0
          ) && renderLinkedInvoices()}

          {renderBillsToReceive()}

          {/* Invoice Actions Section */}
          <div className="invoice-actions-section">
            {invoiceData.options
              .filter(opt => opt.side !== 'adjustment' && opt.id !== 'view_invoice_history')
              .map((option) => {
                // Determine if button should be disabled
                let isDisabled = false;
                let disabledReason = '';
                
                // Check if delivery is not workflowed
                if (deliveryStatus !== 'ACTIVE') {
                  isDisabled = true;
                  disabledReason = 'Delivery must be workflowed first';
                }
                // Raising a document is a write. A delivery-scoped operator can
                // read this delivery's invoices but not create one, so the
                // button is disabled HERE rather than letting them press it and
                // land on a create screen that redirects them out.
                if (!canWriteInvoices) {
                  isDisabled = true;
                  disabledReason = 'Financials or Trade Ops access required to raise invoices';
                }
                // REMOVED: Hard blocks preventing multiple invoice creation
                // Buttons stay enabled to allow partial invoicing with different partners/recaps
                // Note: Validation will be handled during partner/recap selection flow
                
                return (
                  <InvoiceOption 
                    key={option.id}
                    id={option.id}
                    title={option.title} 
                    description={option.description}
                    icon={option.icon}
                    onClick={() => handleOptionClick(option)}
                    isDisabled={isDisabled}
                    disabledReason={disabledReason}
                  />
                );
              })}
          </div>
          
          {/* Adjustment Actions Section - Conditional per side */}
          {(() => {
            const arInvoices = getApprovedARInvoices();
            const apInvoices = getApprovedAPInvoices();
            if (arInvoices.length === 0 && apInvoices.length === 0) return null;

            const creditOption = invoiceData.options.find(o => o.id === 'issue_credit_note');
            const debitOption = invoiceData.options.find(o => o.id === 'issue_debit_note');

            return (
              <div className="adjustment-actions-section">
                <h3 className="adjustment-section-title">Adjustment Actions</h3>
                <p className="section-subtitle">
                  Issue credit notes against outgoing invoices or debit notes against incoming invoices
                </p>
                {arInvoices.length > 0 && creditOption && (
                  <InvoiceOption
                    key={creditOption.id}
                    id={creditOption.id}
                    title={`${creditOption.title}${arInvoices.length === 1 ? ` for ${arInvoices[0].invoice_number}` : ''}`}
                    description={creditOption.description}
                    icon={creditOption.icon}
                    onClick={() => handleOptionClick(creditOption)}
                    isDisabled={!canApproveInvoices}
                    disabledReason="Financials access required to raise adjustment notes"
                    color={creditOption.color}
                  />
                )}
                {apInvoices.length > 0 && debitOption && (
                  <InvoiceOption
                    key={debitOption.id}
                    id={debitOption.id}
                    title={`${debitOption.title}${apInvoices.length === 1 ? ` for ${apInvoices[0].hms_expense_number || apInvoices[0].invoice_number}` : ''}`}
                    description={debitOption.description}
                    icon={debitOption.icon}
                    onClick={() => handleOptionClick(debitOption)}
                    isDisabled={!canApproveInvoices}
                    disabledReason="Financials access required to raise adjustment notes"
                    color={debitOption.color}
                  />
                )}
              </div>
            );
          })()}
          
          {/* Linked Invoices empty state (only when no invoices exist) */}
          {!(linkedInvoices && (
            linkedInvoices.proformas?.length > 0 ||
            linkedInvoices.outgoing?.length > 0 ||
            linkedInvoices.purchaseOrders?.length > 0 ||
            linkedInvoices.incoming?.length > 0 ||
            linkedInvoices.creditNotes?.length > 0 ||
            linkedInvoices.debitNotes?.length > 0
          )) && (
            <div className="invoice-history-section">
              <h3>Linked Invoices</h3>
              {loadingInvoices ? (
                <p className="loading-text">Loading invoices...</p>
              ) : loadProblem ? (
                /* The reported bug in one line: this used to render as "no
                   invoices linked yet". An empty list, a refusal and a
                   half-loaded list are three different answers and the operator
                   has to be able to tell which one they got. The full sentence
                   is in the banner at the top of the panel -- said once, so the
                   two cannot drift into differently-worded explanations of the
                   same thing; this line is here so the section never says
                   "none" about a load that failed. */
                <p className="no-invoices-text">
                  <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                  {loadProblem.kind === 'truncated'
                    ? 'Only part of this list could be loaded — see the notice at the top of this panel.'
                    : 'This list could not be loaded — see the notice at the top of this panel.'}
                </p>
              ) : (
                <p className="no-invoices-text">
                  {canWriteInvoices
                    ? 'No invoices linked yet. Create an invoice above to get started.'
                    : 'No invoices have been raised against this delivery yet.'}
                  {deliveryStatus === 'ACTIVE' && canApproveInvoices && (
                    <>
                      <br/><br/>
                      Once invoices are approved, adjustment options will appear above to issue credit or debit notes for corrections and modifications.
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Invoice Selection Modal */}
      {showInvoiceSelectionModal && (
        <InvoiceSelectionModal
          isOpen={showInvoiceSelectionModal}
          onClose={() => setShowInvoiceSelectionModal(false)}
          invoices={selectedAdjustmentType === 'credit' ? getApprovedARInvoices() : getApprovedAPInvoices()}
          onSelect={handleInvoiceSelected}
          type={selectedAdjustmentType}
        />
      )}
      
      {/* Partner Selection Modal */}
      {showPartnerSelectionModal && deliveryData && pendingInvoiceOption && (
        <PartnerSelectionModal
          isOpen={showPartnerSelectionModal}
          onClose={() => {
            setShowPartnerSelectionModal(false);
            setPendingInvoiceOption(null);
            setDeliveryData(null);
          }}
          partnerType={
            pendingInvoiceOption.id === 'issue_proforma_invoice' || 
            pendingInvoiceOption.id === 'issue_final_invoice' 
              ? 'buyer' 
              : 'seller'
          }
          partners={
            pendingInvoiceOption.id === 'issue_proforma_invoice' || 
            pendingInvoiceOption.id === 'issue_final_invoice'
              ? deliveryData.buyer_contractual_list || []
              : deliveryData.seller_contractual_list || []
          }
          onSelect={handlePartnerSelected}
        />
      )}
      
      {/* Which supplier a supplier invoice is from, when the deal has several */}
      {billSupplierOption && (
        <PartnerSelectionModal
          isOpen
          onClose={() => setBillSupplierOption(null)}
          partnerType="seller"
          partners={billSuppliers().map((g) => ({ crm_id: g.supplier_id, name: g.supplier_name }))}
          onSelect={(supplierId) => {
            const option = billSupplierOption;
            setBillSupplierOption(null);
            const group = billSuppliers().find((g) => g.supplier_id === supplierId);
            if (group) startSupplierInvoice(group, option);
          }}
        />
      )}

      {/* Recap Selection Modal */}
      {showRecapSelectionModal && deliveryData && pendingInvoiceOption && (
        <RecapSelectionModal
          isOpen={showRecapSelectionModal}
          onClose={() => {
            setShowRecapSelectionModal(false);
            setPendingInvoiceOption(null);
            setDeliveryData(null);
            setSelectedPartnerId(null);
          }}
          recapType={
            pendingInvoiceOption.id === 'issue_proforma_invoice' || 
            pendingInvoiceOption.id === 'issue_final_invoice' 
              ? 'sales' 
              : 'purchase'
          }
          recaps={
            pendingInvoiceOption.id === 'issue_proforma_invoice' || 
            pendingInvoiceOption.id === 'issue_final_invoice'
              ? deliveryData.sales_recaps_data || []
              : deliveryData.purchase_recaps_data || []
          }
          selectedPartnerId={selectedPartnerId}
          onConfirm={handleRecapSelected}
        />
      )}

      {/* Proforma Conversion Choice Modal */}
      {showProformaChoiceModal && pendingProforma && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => {
            setShowProformaChoiceModal(false);
            setPendingProforma(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '460px',
              width: '90vw',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  borderRadius: '50%',
                  backgroundColor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    style={{ color: '#2563eb', fontSize: '1.125rem' }}
                  />
                </div>
                <h3 style={{ margin: 0, color: '#111827', fontSize: '1.05rem', fontWeight: '600' }}>
                  Existing Proforma Found
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowProformaChoiceModal(false);
                  setPendingProforma(null);
                }}
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  border: 'none',
                  background: 'none',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              padding: '0.875rem 1rem',
              marginBottom: '1.25rem'
            }}>
              <p style={{ margin: 0, color: '#1e40af', fontSize: '0.875rem', lineHeight: '1.5' }}>
                This delivery has an unconverted proforma
                <strong style={{ marginLeft: '0.25rem' }}>
                  {pendingProforma.invoice_number}
                </strong>.
                How would you like to proceed?
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <button
                onClick={handleProformaConvert}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              >
                <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '1rem', flexShrink: 0 }} />
                <span>Convert proforma to final invoice</span>
              </button>
              <button
                onClick={handleCreateNewOutgoing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
              >
                <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ fontSize: '1rem', flexShrink: 0 }} />
                <span>Create a new outgoing invoice</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePanel; 